import { actionProps } from "@/lib/controlAnalytics";
import FilterPopover, { FilterChoice } from "@/components/common/FilterPopover";
import { WITHIN_CHIP, WITHIN_MILES, withinSummary, type JobsView } from "@/lib/jobs";
import type { FilterToggle } from "./JobsFilterMenus";

const ANY_DISTANCE = "any";

/**
 * How far from the person a posting worked on site may be: 5, 25 or 50
 * miles, or any distance. Remote work stays in, since it has no distance.
 * Measured from where the edge says they are; when it cannot say, the search
 * is of every distance, the button stops reading as in force and the panel
 * says why. The places are GeoNames', which asks to be credited where they
 * are used.
 */
export default function JobsDistanceFilter({
    within,
    nowhere,
    toggle,
    onChange,
}: {
    within: JobsView["within"];
    /** The catalogue could not tell where the person is. */
    nowhere: boolean;
    toggle: FilterToggle;
    onChange: (patch: Partial<JobsView>) => void;
}) {
    return (
        <FilterPopover
            {...toggle}
            label="Distance"
            summary={withinSummary(within)}
            selected={within !== null && !nowhere}
            analyticsField="distance"
        >
            {() => (
                <div className="p-1.5">
                    <div role="radiogroup" aria-label="Distance" className="flex flex-col gap-0.5">
                        <FilterChoice
                            type="radio"
                            name="distance"
                            checked={within === null}
                            action="job_distance_selected"
                            context={{ source: "Jobs", option: ANY_DISTANCE, next_state: false }}
                            onChange={() => onChange({ within: null })}
                        >
                            Any distance
                        </FilterChoice>
                        {WITHIN_MILES.map((miles) => (
                            <FilterChoice
                                key={miles}
                                type="radio"
                                name="distance"
                                checked={within === miles}
                                action="job_distance_selected"
                                context={{
                                    source: "Jobs",
                                    option: String(miles),
                                    next_state: true,
                                }}
                                onChange={() => onChange({ within: miles })}
                            >
                                {WITHIN_CHIP[miles]}
                            </FilterChoice>
                        ))}
                    </div>
                    {within !== null && (
                        <p
                            className="m-0 border-t border-line-soft px-2.5 pt-2 pb-1.5 text-[11.5px] leading-[1.45] text-fg-muted"
                            role="status"
                        >
                            {nowhere ? (
                                "We could not tell where you are, so this shows every distance."
                            ) : (
                                <>
                                    On site within {WITHIN_CHIP[within]} of you, and remote work.
                                    Places from{" "}
                                    <a
                                        {...actionProps("job_distance_credit_clicked", {
                                            source: "Jobs",
                                        })}
                                        href="https://www.geonames.org"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="underline"
                                    >
                                        GeoNames
                                    </a>
                                    .
                                </>
                            )}
                        </p>
                    )}
                </div>
            )}
        </FilterPopover>
    );
}
